import { useCallback, useState } from 'react';

/* Generic localStorage hook with SSR-safe read and silent error handling.     */
/* Falls back to defaultValue on any storage error (private browsing, quota).   */
export function useLocalStorage<T>(
  key: string,
  defaultValue: T,
): [T, (next: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const stored = localStorage.getItem(key);
      return stored !== null ? (JSON.parse(stored) as T) : defaultValue;
    } catch {
      return defaultValue;
    }
  });

  const set = useCallback(
    (next: T | ((prev: T) => T)) => {
      setValue((prev) => {
        const resolved = typeof next === 'function' ? (next as (p: T) => T)(prev) : next;
        try {
          localStorage.setItem(key, JSON.stringify(resolved));
        } catch { /* ignore quota / security errors */ }
        return resolved;
      });
    },
    [key],
  );

  return [value, set];
}
